import { isEqual } from 'lodash';
import { diffUtility, getAiToolkit } from '@tiptap-pro/ai-toolkit';
import { type JSONContent, type Range, type Editor } from '@tiptap/react';
import { Node as ProseMirrorNode, Slice } from '@tiptap/pm/model';
import type { Node } from '@tiptap/pm/model';

import {
  setSelectedSuggestion,
  isEditorComponentMounted,
  getEditorHydratedReview,
  setEditorHydratedReview,
  getEditorHydrationGeneration,
  bumpEditorHydrationGeneration,
  setTerminalResolutionStarted,
  type SuggestionDiffConfig,
} from '@/hooks/llm/editor-suggestion-config';
import { trySetEditorSelection } from '@/hooks/use-suggestion-handlers/suggestion-editor-navigation';
import { isEditorAlive } from '@/utils/editor-utils';

type ToolkitSuggestion = ReturnType<ReturnType<typeof getAiToolkit>['getSuggestions']>[number];
type StructuredCloneableValue =
  | null
  | string
  | number
  | boolean
  | StructuredCloneableValue[]
  | { [key: string]: StructuredCloneableValue };
type StructuredCloneableObject = { [key: string]: StructuredCloneableValue };

type PersistedSliceJson = {
  content?: JSONContent[];
  openStart: number;
  openEnd: number;
};

type SerializedReplacementContent = string | PersistedSliceJson | null;

type ReviewReplacementOption = {
  id: string;
  content: SerializedReplacementContent;
  metadata?: StructuredCloneableObject;
};

type ReviewReplacementOptionInput = {
  id: string;
  content: string | Slice | SerializedReplacementContent;
  metadata?: unknown;
};

type ReviewDisplayOptions = {
  showReplacement?: boolean;
  hideCurrentContent?: boolean;
  showSubChanges?: boolean;
  attributes?: StructuredCloneableObject;
  replacementAttributes?: StructuredCloneableObject;
  subChangeAttributes?: StructuredCloneableObject;
  replacementSubChangeAttributes?: StructuredCloneableObject;
};

export type ReviewSuggestion = {
  id: string;
  range: Range;
  rangeInOtherDoc?: Range;
  replacementOptions: ReviewReplacementOption[];
  metadata?: StructuredCloneableObject;
  displayOptions?: ReviewDisplayOptions;
  reviewMode?: 'preview' | 'review';
  isInlineGroup?: boolean;
};

export type PendingReview = {
  originalDocJson: JSONContent;
  reviewDocJson: JSONContent;
  suggestions: ReviewSuggestion[];
  // Review-telemetry counters, persisted so a mid-review reload can still report the final
  // note_generation_reviewed event. Optional because reviews created before this change lack them.
  acceptedCount?: number;
  totalCount?: number;
  createdAtMs?: number;
};

type ReviewCounters = { acceptedCount: number; totalCount: number; createdAtMs: number };

// The review-telemetry counters are seeded together, so a single predicate decides whether a review
// carries them. Reviews persisted before the counters existed lack all three and are skipped.
export const hasReviewCounters = (pendingReview: PendingReview): pendingReview is PendingReview & ReviewCounters =>
  pendingReview.acceptedCount != null && pendingReview.totalCount != null && pendingReview.createdAtMs != null;

type ReviewSuggestionInput = Omit<ReviewSuggestion, 'replacementOptions' | 'displayOptions'> & {
  replacementOptions: ReviewReplacementOptionInput[];
  displayOptions?: ToolkitSuggestion['displayOptions'] | ReviewDisplayOptions;
};

type SuggestionSignature = Omit<ReviewSuggestion, 'id' | 'replacementOptions'> & {
  replacementOptions: Array<Omit<ReviewReplacementOption, 'id'>>;
};

// RxDB/Firestore persistence can see hydrated proxy objects here, so coerce
// nested values into plain structured-cloneable JSON before writing.
const toStructuredCloneableJson = (value: unknown): StructuredCloneableValue | undefined => {
  if (value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      const normalizedItem = toStructuredCloneableJson(item);
      return normalizedItem === undefined ? null : normalizedItem;
    });
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, nestedValue]) => {
        const normalizedValue = toStructuredCloneableJson(nestedValue);
        return normalizedValue === undefined ? [] : [[key, normalizedValue]];
      })
    );
  }

  return undefined;
};

const toStructuredCloneableObject = (value: unknown): StructuredCloneableObject | undefined => {
  const normalizedValue = toStructuredCloneableJson(value);
  return normalizedValue && typeof normalizedValue === 'object' && !Array.isArray(normalizedValue)
    ? (normalizedValue as StructuredCloneableObject)
    : undefined;
};

const serializeReplacementContent = (content: string | Slice | PersistedSliceJson | null): SerializedReplacementContent => {
  if (content == null || typeof content === 'string') {
    return content;
  }

  return toStructuredCloneableJson(content instanceof Slice ? content.toJSON() : content) as PersistedSliceJson | null;
};

const deserializeReplacementContent = (editor: Editor, content: SerializedReplacementContent): string | Slice =>
  typeof content === 'string' ? content : Slice.fromJSON(editor.state.schema, content);

// One field descriptor drives every review-suggestion projection so the serialized JSON, the
// deserialized runtime shape, and the restore-matching signature can never drift apart. The persisted
// payload is durable in production RxDB, so each descriptor pins the exact present/absent decision and
// per-projection value; conditional fields must stay absent (never `undefined`) and the key order here
// is the serialized key order.
type FieldProjection = { include: boolean; value?: unknown };
const includeField = (value: unknown): FieldProjection => ({ include: true, value });
const omitField: FieldProjection = { include: false };
const includeWhenPresent = (present: boolean, value: unknown): FieldProjection => (present ? includeField(value) : omitField);

// Display options are their own descriptor table. Booleans persist when explicitly set (`!= null`);
// attribute maps persist only when they coerce to a non-empty structured-cloneable object.
type DisplayFieldDescriptor = { key: keyof ReviewDisplayOptions; project: (value: unknown) => FieldProjection };
const displayBooleanField = (key: keyof ReviewDisplayOptions): DisplayFieldDescriptor => ({
  key,
  project: (value) => includeWhenPresent(value != null, value),
});
const displayObjectField = (key: keyof ReviewDisplayOptions): DisplayFieldDescriptor => ({
  key,
  project: (value) => {
    const coerced = toStructuredCloneableObject(value);
    return includeWhenPresent(!!coerced, coerced);
  },
});
const DISPLAY_OPTION_FIELDS: DisplayFieldDescriptor[] = [
  displayBooleanField('showReplacement'),
  displayBooleanField('hideCurrentContent'),
  displayBooleanField('showSubChanges'),
  displayObjectField('attributes'),
  displayObjectField('replacementAttributes'),
  displayObjectField('subChangeAttributes'),
  displayObjectField('replacementSubChangeAttributes'),
];

const buildFromProjections = (entries: Array<[string, FieldProjection]>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, projection] of entries) {
    if (projection.include) {
      result[key] = projection.value;
    }
  }
  return result;
};

const serializeDisplayOptions = (
  displayOptions?: ToolkitSuggestion['displayOptions'] | ReviewDisplayOptions
): ReviewDisplayOptions | undefined => {
  if (!displayOptions) return undefined;

  const serialized = buildFromProjections(
    DISPLAY_OPTION_FIELDS.map((field) => [field.key, field.project((displayOptions as Record<string, unknown>)[field.key])])
  ) as ReviewDisplayOptions;

  return Object.keys(serialized).length ? serialized : undefined;
};

const toPlainReviewReplacementOption = (replacementOption: ReviewReplacementOptionInput): ReviewReplacementOption => {
  const metadata = toStructuredCloneableObject(replacementOption.metadata);

  return {
    id: replacementOption.id,
    content: serializeReplacementContent(replacementOption.content),
    ...(metadata ? { metadata } : {}),
  };
};

const toRuntimeReplacementOption = (editor: Editor, replacementOption: ReviewReplacementOption) => ({
  id: replacementOption.id,
  content: deserializeReplacementContent(editor, replacementOption.content),
  ...(replacementOption.metadata ? { metadata: replacementOption.metadata } : {}),
});

const toSignatureReplacementOption = ({ content, metadata }: ReviewReplacementOption): Omit<ReviewReplacementOption, 'id'> => ({
  content,
  ...(metadata ? { metadata } : {}),
});

// Ordered descriptor table for the top-level suggestion fields. `toPlain` reads a hydrated input and
// coerces to structured-cloneable JSON; `toRuntime` rebuilds the editor-facing shape; `toSignature`
// derives the id-free identity used to re-match runtime suggestions on restore.
type ReviewSuggestionFieldDescriptor = {
  key: keyof ReviewSuggestion;
  inSignature: boolean;
  toPlain: (suggestion: ReviewSuggestionInput) => FieldProjection;
  toRuntime: (suggestion: ReviewSuggestion, editor: Editor) => FieldProjection;
  toSignature: (suggestion: ReviewSuggestion) => FieldProjection;
};

const REVIEW_SUGGESTION_FIELDS: ReviewSuggestionFieldDescriptor[] = [
  {
    key: 'id',
    inSignature: false,
    toPlain: (suggestion) => includeField(suggestion.id),
    toRuntime: (suggestion) => includeField(suggestion.id),
    toSignature: () => omitField,
  },
  {
    key: 'range',
    inSignature: true,
    toPlain: (suggestion) => includeField(toStructuredCloneableJson(suggestion.range) as Range),
    toRuntime: (suggestion) => includeField(suggestion.range),
    toSignature: (suggestion) => includeField(suggestion.range),
  },
  {
    key: 'rangeInOtherDoc',
    inSignature: true,
    toPlain: (suggestion) =>
      includeWhenPresent(!!suggestion.rangeInOtherDoc, toStructuredCloneableJson(suggestion.rangeInOtherDoc) as Range),
    toRuntime: (suggestion) => includeWhenPresent(!!suggestion.rangeInOtherDoc, suggestion.rangeInOtherDoc),
    toSignature: (suggestion) => includeWhenPresent(!!suggestion.rangeInOtherDoc, suggestion.rangeInOtherDoc),
  },
  {
    key: 'replacementOptions',
    inSignature: true,
    toPlain: (suggestion) => includeField(suggestion.replacementOptions.map(toPlainReviewReplacementOption)),
    toRuntime: (suggestion, editor) =>
      includeField(suggestion.replacementOptions.map((option) => toRuntimeReplacementOption(editor, option))),
    toSignature: (suggestion) => includeField(suggestion.replacementOptions.map(toSignatureReplacementOption)),
  },
  {
    key: 'metadata',
    inSignature: true,
    toPlain: (suggestion) => {
      const metadata = toStructuredCloneableObject(suggestion.metadata);
      return includeWhenPresent(!!metadata, metadata);
    },
    toRuntime: (suggestion) => includeWhenPresent(!!suggestion.metadata, suggestion.metadata),
    toSignature: (suggestion) => includeWhenPresent(!!suggestion.metadata, suggestion.metadata),
  },
  {
    key: 'displayOptions',
    inSignature: true,
    toPlain: (suggestion) => {
      const displayOptions = serializeDisplayOptions(suggestion.displayOptions);
      return includeWhenPresent(!!displayOptions, displayOptions);
    },
    toRuntime: (suggestion) => includeWhenPresent(!!suggestion.displayOptions, suggestion.displayOptions),
    toSignature: (suggestion) => includeWhenPresent(!!suggestion.displayOptions, suggestion.displayOptions),
  },
  {
    key: 'reviewMode',
    inSignature: true,
    toPlain: (suggestion) => includeWhenPresent(!!suggestion.reviewMode, suggestion.reviewMode),
    toRuntime: (suggestion) => includeWhenPresent(!!suggestion.reviewMode, suggestion.reviewMode),
    toSignature: (suggestion) => includeWhenPresent(!!suggestion.reviewMode, suggestion.reviewMode),
  },
  {
    key: 'isInlineGroup',
    inSignature: true,
    toPlain: (suggestion) => includeWhenPresent(suggestion.isInlineGroup != null, suggestion.isInlineGroup),
    toRuntime: (suggestion) => includeWhenPresent(suggestion.isInlineGroup != null, suggestion.isInlineGroup),
    toSignature: (suggestion) => includeWhenPresent(suggestion.isInlineGroup != null, suggestion.isInlineGroup),
  },
];

export const toPlainReviewSuggestion = (suggestion: ReviewSuggestionInput): ReviewSuggestion =>
  buildFromProjections(REVIEW_SUGGESTION_FIELDS.map((field) => [field.key, field.toPlain(suggestion)])) as ReviewSuggestion;

export const toPlainPendingReview = (pendingReview: PendingReview): PendingReview => ({
  originalDocJson: toStructuredCloneableJson(pendingReview.originalDocJson) as JSONContent,
  reviewDocJson: toStructuredCloneableJson(pendingReview.reviewDocJson) as JSONContent,
  suggestions: pendingReview.suggestions.map(toPlainReviewSuggestion),
  ...(hasReviewCounters(pendingReview)
    ? {
        acceptedCount: pendingReview.acceptedCount,
        totalCount: pendingReview.totalCount,
        createdAtMs: pendingReview.createdAtMs,
      }
    : {}),
});

const toSuggestionSignature = (suggestion: ReviewSuggestion): SuggestionSignature =>
  buildFromProjections(
    REVIEW_SUGGESTION_FIELDS.filter((field) => field.inSignature).map((field) => [field.key, field.toSignature(suggestion)])
  ) as SuggestionSignature;

const buildDiffReviewOptions = (pendingReview: PendingReview): SuggestionDiffConfig['reviewOptions'] | undefined => {
  const firstSuggestion = pendingReview.suggestions[0];
  if (!firstSuggestion) return undefined;

  return {
    ...(firstSuggestion.reviewMode ? { mode: firstSuggestion.reviewMode } : {}),
    ...(firstSuggestion.displayOptions ? { displayOptions: firstSuggestion.displayOptions } : {}),
    diffUtilityOptions: {
      mode: 'smart',
    },
  };
};

const syncEditorDocJson = (editor: Editor, docJson: JSONContent): void => {
  const nextDoc = ProseMirrorNode.fromJSON(editor.state.schema, docJson);
  if (editor.state.doc.eq(nextDoc)) {
    return;
  }

  const tr = editor.state.tr.replace(0, editor.state.doc.content.size, new Slice(nextDoc.content, 0, 0));
  editor.view.dispatch(tr);
};

const selectSuggestionRange = (editor: Editor, range: Range): boolean => trySetEditorSelection(editor, range);

const restoreRuntimeSuggestions = (editor: Editor, pendingReview: PendingReview): ToolkitSuggestion[] | null => {
  if (!pendingReview.suggestions.length) {
    return [];
  }

  const toolkit = getAiToolkit(editor);
  const otherDoc = ProseMirrorNode.fromJSON(editor.state.schema, pendingReview.originalDocJson);
  const reviewOptions = buildDiffReviewOptions(pendingReview);

  toolkit.setSuggestionsFromDiff({
    doc: otherDoc,
    ...(reviewOptions ? { reviewOptions } : {}),
  });

  const runtimeSuggestions = toolkit.getSuggestions() as ToolkitSuggestion[];
  const availableRuntimeSuggestions = [...runtimeSuggestions];
  const availableRuntimeSignatures = serializeReviewSuggestions(runtimeSuggestions).map(toSuggestionSignature);

  const restoredSuggestions = pendingReview.suggestions.map((suggestion) => {
    const matchIndex = availableRuntimeSignatures.findIndex((runtimeSignature) =>
      isEqual(runtimeSignature, toSuggestionSignature(suggestion))
    );

    if (matchIndex < 0) {
      return null;
    }

    const runtimeSuggestion = availableRuntimeSuggestions.splice(matchIndex, 1)[0];
    availableRuntimeSignatures.splice(matchIndex, 1);

    return {
      ...runtimeSuggestion,
      id: suggestion.id,
      replacementOptions: runtimeSuggestion.replacementOptions.map((replacementOption, index) => ({
        ...replacementOption,
        id: suggestion.replacementOptions[index]?.id ?? replacementOption.id,
        ...(suggestion.replacementOptions[index]?.metadata
          ? { metadata: suggestion.replacementOptions[index].metadata }
          : replacementOption.metadata
          ? { metadata: replacementOption.metadata }
          : {}),
      })),
    };
  });

  if (restoredSuggestions.some((suggestion) => suggestion == null)) {
    return null;
  }

  return restoredSuggestions as ToolkitSuggestion[];
};

export const serializeReviewSuggestions = (suggestions: ReviewSuggestionInput[]): ReviewSuggestion[] =>
  suggestions.map(toPlainReviewSuggestion);

export const getPlainReviewSuggestions = (toolkit: ReturnType<typeof getAiToolkit>): ReviewSuggestion[] =>
  serializeReviewSuggestions(toolkit.getSuggestions() as ToolkitSuggestion[]);

export const deserializeReviewSuggestions = (editor: Editor, suggestions: ReviewSuggestion[]): ToolkitSuggestion[] =>
  suggestions.map(
    (suggestion) =>
      buildFromProjections(
        REVIEW_SUGGESTION_FIELDS.map((field) => [field.key, field.toRuntime(suggestion, editor)])
      ) as unknown as ToolkitSuggestion
  );

export const createPendingReview = ({
  editor,
  beforeDoc,
  config,
}: {
  editor: Editor;
  beforeDoc: Node;
  config: SuggestionDiffConfig;
}): PendingReview | null => {
  const suggestions = createReviewSuggestionsFromDiff(editor, config);
  if (!suggestions.length) return null;

  const serializedSuggestions = serializeReviewSuggestions(suggestions);

  return {
    originalDocJson: beforeDoc.toJSON() as JSONContent,
    reviewDocJson: editor.state.doc.toJSON() as JSONContent,
    suggestions: serializedSuggestions,
    acceptedCount: 0,
    totalCount: suggestions.length,
    createdAtMs: Date.now(),
  };
};

const createReviewSuggestionsFromDiff = (editor: Editor, config: SuggestionDiffConfig): ReviewSuggestionInput[] => {
  const reviewOptions = config.reviewOptions;
  const changes = diffUtility({
    schema: editor.schema,
    ...(reviewOptions?.diffUtilityOptions ?? {}),
    docA: editor.state.doc,
    docB: config.doc,
  });

  return changes.map((change, index) => ({
    id: `suggestion-${index + 1}`,
    range: change.rangeA,
    rangeInOtherDoc: change.rangeB,
    replacementOptions: [
      {
        id: '1',
        content: config.doc.slice(change.rangeB.from, change.rangeB.to),
      },
    ],
    ...(reviewOptions?.displayOptions ? { displayOptions: reviewOptions.displayOptions } : {}),
    ...(reviewOptions?.metadata ? { metadata: reviewOptions.metadata } : {}),
    reviewMode: reviewOptions?.mode === 'review' ? 'review' : 'preview',
    ...(change.isInlineGroup != null ? { isInlineGroup: change.isInlineGroup } : {}),
  }));
};

export const restorePendingReview = (editor: Editor, pendingReview: PendingReview): (() => void) => {
  let isCleanedUp = false;
  const armedGeneration = getEditorHydrationGeneration(editor);

  const frameId = requestAnimationFrame(() => {
    if (isCleanedUp || !isEditorAlive(editor) || !isEditorComponentMounted(editor)) return;
    // Any synchronous review-state advance since arming (click-time heal apply, a decision's
    // partial-resolution re-mark, terminal resolution start) supersedes this queued frame;
    // re-applying would resurrect state the user has already moved past.
    if (getEditorHydrationGeneration(editor) !== armedGeneration) return;

    applyPendingReview(editor, pendingReview);
  });

  return () => {
    isCleanedUp = true;
    cancelAnimationFrame(frameId);
  };
};

export const applyPendingReview = (editor: Editor, pendingReview: PendingReview): void => {
  if (!isEditorAlive(editor)) return;

  // Hydrating a different payload starts a new review round on this editor; a terminal flag
  // left over from the previous round must not gate the new one.
  if (!isReviewHydrated(editor, pendingReview)) {
    setTerminalResolutionStarted(editor, false);
  }

  if (!isEqual(editor.state.doc.toJSON(), pendingReview.reviewDocJson)) {
    syncEditorDocJson(editor, pendingReview.reviewDocJson);
  }

  const suggestions =
    restoreRuntimeSuggestions(editor, pendingReview) ?? deserializeReviewSuggestions(editor, pendingReview.suggestions);

  const toolkit = getAiToolkit(editor);
  const firstPersistedSuggestion = pendingReview.suggestions[0];
  if (firstPersistedSuggestion?.range) {
    selectSuggestionRange(editor, firstPersistedSuggestion.range);
  }

  toolkit.setSuggestions(suggestions);

  const firstSuggestion = suggestions[0];
  if (firstSuggestion?.range) {
    selectSuggestionRange(editor, firstSuggestion.range);
  }
  if (firstSuggestion?.id) {
    setSelectedSuggestion(editor, firstSuggestion.id);
  }

  markReviewHydrated(editor, pendingReview);
};

// Tracks the persisted review payload each mounted editor currently reflects, payload-to-payload.
// The editor leads the persisted doc during local resolution, so restore decisions must compare
// incoming payloads against what this editor already processed — never against live editor state.
export const markReviewHydrated = (editor: Editor, pendingReview: PendingReview): void => {
  setEditorHydratedReview(editor, toPlainPendingReview(pendingReview));
  bumpEditorHydrationGeneration(editor);
};

export const clearHydratedReview = (editor: Editor): void => {
  setEditorHydratedReview(editor, undefined);
  setTerminalResolutionStarted(editor, false);
};

export const isReviewHydrated = (editor: Editor, pendingReview: PendingReview): boolean =>
  isEqual(getEditorHydratedReview(editor), toPlainPendingReview(pendingReview));
